import { FetchQueryOptions, useQueryClient } from "@tanstack/react-query";
import { CommonAgentMomentList, CommonAPI } from "../types";
import { agentQueries, eventStreamQueries } from "../queries";

export type AgentMomentCustomPrefetch = ({
  sessionId,
  agentId,
  momentId,
}: {
  sessionId: string;
  agentId: string;
  momentId: number;
}) => AgentMomentCustomPrefetchQueries;

export type AgentMomentCustomPrefetchQueries = Array<FetchQueryOptions>;

const useAgentMomentPrefetch = ({
  agentId,
  momentId,
  momentList,
  commonAPI,
  customPrefetch,
}: {
  agentId: string;
  momentId: number | null;
  momentList?: CommonAgentMomentList;
  commonAPI: CommonAPI;
  customPrefetch?: AgentMomentCustomPrefetch;
}) => {
  const queryClient = useQueryClient();

  const prefetchMomentWindow = () => {
    if (!momentList || !momentId) {
      return;
    }

    const momentsToFetch: Array<{ moment_id: number; session_id: string }> = [];
    const momentIndex = momentList.moments.findIndex(
      (moment) => moment.moment_id === momentId
    );

    if (momentIndex > 0) {
      const earlierMoment = momentList.moments[momentIndex - 1];
      if (earlierMoment) {
        momentsToFetch.push(earlierMoment);
      }
    }
    if (momentIndex < momentList.moments.length - 1) {
      const laterMoment = momentList.moments[momentIndex + 1];
      if (laterMoment) {
        momentsToFetch.push(laterMoment);
      }
    }

    momentsToFetch.forEach(({ moment_id, session_id }) => {
      queryClient.prefetchQuery({
        queryKey: eventStreamQueries.getDetail(session_id, agentId, moment_id),
        queryFn: () =>
          commonAPI.fetchEventStreamDetail(session_id, agentId, moment_id),
        staleTime: Infinity,
      });

      queryClient.prefetchQuery({
        queryKey: agentQueries.getPublicState(session_id, agentId, moment_id),
        queryFn: () =>
          commonAPI.fetchAgentPublicState({
            sessionId: session_id,
            agentId,
            momentId: moment_id,
          }),
        staleTime: Infinity,
      });

      queryClient.prefetchQuery({
        queryKey: agentQueries.getPresentation(session_id, agentId, moment_id),
        queryFn: () =>
          commonAPI.fetchAgentPresentation({
            sessionId: session_id,
            agentId,
            momentId: moment_id,
          }),
        staleTime: Infinity,
      });

      customPrefetch?.({
        sessionId: session_id,
        agentId,
        momentId: moment_id,
      })?.forEach((query) => {
        queryClient.prefetchQuery(query);
      });
    });
  };

  return {
    prefetchMomentWindow,
  };
};

export default useAgentMomentPrefetch;
